import { useMemo, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useCustomTheme, TTheme } from '@/core/theme'
import { useRouting } from '@/core/hooks/useRouting'

interface UseTabsRoutingProps {
    tabList: TTabsList
}

interface VisibleTab {
    tabTitle: string
    path: string
    id?: string
    analyticsEvent?: AnalyticsEvents
    analyticsParams?: Record<string, unknown>
}

interface UseTabsRoutingReturn {
    activeTab: string
    handleTabClick: (path: string) => void
    visibleTabs: VisibleTab[]
    theme: TTheme
}

export const useTabsRouting = ({
    tabList,
}: UseTabsRoutingProps): UseTabsRoutingReturn => {
    const theme = useCustomTheme() as TTheme
    const { navigateTo } = useRouting()
    const { pathname } = useLocation()
    const [activeTab, setActiveTab] = useState(pathname)

    useEffect(() => {
        setActiveTab(pathname)
    }, [pathname])

    const visibleTabs = useMemo(
        () => tabList.filter((tab) => !tab.hide),
        [tabList]
    )

    const handleTabClick = (path: string) => {
        setActiveTab(path)

        const tab = visibleTabs.find((t) => t.path === path)

        navigateTo({
            path,
            event: tab?.analyticsEvent
                ? { name: tab.analyticsEvent, params: tab.analyticsParams }
                : undefined,
        })
    }

    return {
        activeTab,
        handleTabClick,
        visibleTabs,
        theme,
    }
}
